import { inject, Injectable } from '@angular/core';
import { catchError, forkJoin, map, Observable, of, switchMap } from 'rxjs';
import { ApiService, Ticket, TicketWalletBalanceLeg } from '@hypenation/tickets';

export interface TicketWithOrder extends Ticket {
  orderNumber: string;
}

/** What a customer holds on one event, and what is on those tickets' wallets. */
export interface CustomerEventSummary {
  eventUid: string;
  name: string;
  date?: string;
  location?: string;
  walletsEnabled: boolean;
  ticketCount: number;
  /** Totalled per currency across this event's tickets. Empty when nothing is loaded on them. */
  walletTotals: { currencyCode: string; balance: number }[];
}

export interface WalletLookup {
  /** Balance legs per ticket uid. Empty for a ticket whose wallet holds nothing. */
  balances: Map<string, TicketWalletBalanceLeg[]>;
  /** Ticket uids whose wallet lookup succeeded — a wallet exists, whatever it holds. */
  existing: Set<string>;
}

/**
 * Loads the customer's own tickets and their wallet balances.
 *
 * Extracted so the events section and My Tickets read the same pipeline rather than each
 * re-implementing orders → tickets → balances. Getting a customer's tickets is three hops
 * (completed orders, then tickets per order, then a balance per wallet-enabled ticket), which is
 * exactly the kind of thing that drifts when it is written twice.
 */
@Injectable({ providedIn: 'root' })
export class MyTicketsLoaderService {
  private readonly apiService = inject(ApiService);

  /** Every ticket on the customer's completed orders, tagged with the order it came from. */
  loadTickets(email: string): Observable<TicketWithOrder[]> {
    return this.apiService.getAllMyOrdersByEmail(email).pipe(
      switchMap((res) => {
        const completedOrders = (res.body ?? []).filter((o) => o.status === 'COMPLETED');
        if (!completedOrders.length) return of([] as TicketWithOrder[]);

        return forkJoin(
          completedOrders.map((order) =>
            this.apiService.getTicketByOrderNumber(order.orderNumber).pipe(
              map((r) =>
                (r.body ?? []).map(
                  (t) => ({ ...t, orderNumber: order.orderNumber } as TicketWithOrder)
                )
              ),
              // One unreadable order should not cost the customer the rest of their tickets.
              catchError(() => of([] as TicketWithOrder[]))
            )
          )
        ).pipe(map((responses) => responses.flat()));
      })
    );
  }

  /**
   * Balances for tickets on wallet-enabled events.
   *
   * A per-ticket failure is swallowed on purpose: a ticket on a wallets-enabled event legitimately
   * has no wallet when it was issued before the flag was turned on, and that is not something to
   * alarm a customer about. It is recorded as "no wallet" rather than "zero balance", because those
   * are different things — see the `existing` set.
   */
  loadWalletBalances(tickets: TicketWithOrder[]): Observable<WalletLookup> {
    const walletTickets = tickets.filter((t) => t.event?.walletsEnabled);
    const empty: WalletLookup = { balances: new Map(), existing: new Set() };
    if (!walletTickets.length) return of(empty);

    return forkJoin(
      walletTickets.map((ticket) =>
        this.apiService.getMyTicketWalletBalance(ticket.uid).pipe(
          map((res) => ({
            uid: ticket.uid,
            exists: true,
            balances: res.body?.balances ?? [],
          })),
          catchError(() =>
            of({ uid: ticket.uid, exists: false, balances: [] as TicketWalletBalanceLeg[] })
          )
        )
      )
    ).pipe(
      map((results) => {
        const lookup: WalletLookup = { balances: new Map(), existing: new Set() };
        for (const result of results) {
          lookup.balances.set(result.uid, result.balances);
          if (result.exists) lookup.existing.add(result.uid);
        }
        return lookup;
      })
    );
  }

  /**
   * Groups tickets into one entry per event, newest event first.
   *
   * Wallet balances are totalled per currency within an event and never across events — those funds
   * are only spendable at their own event, so a single combined figure would be meaningless.
   */
  summariseByEvent(
    tickets: TicketWithOrder[],
    wallets: WalletLookup
  ): CustomerEventSummary[] {
    const byEvent = new Map<string, CustomerEventSummary>();

    for (const ticket of tickets) {
      const eventUid = ticket.event?.uid;
      if (!eventUid) continue;

      let summary = byEvent.get(eventUid);
      if (!summary) {
        summary = {
          eventUid,
          name: ticket.event?.name ?? 'Untitled event',
          date: ticket.event?.date,
          location: ticket.event?.location?.description ?? 'Online',
          walletsEnabled: !!ticket.event?.walletsEnabled,
          ticketCount: 0,
          walletTotals: [],
        };
        byEvent.set(eventUid, summary);
      }
      summary.ticketCount += 1;
    }

    for (const summary of byEvent.values()) {
      if (!summary.walletsEnabled) continue;
      const totals = new Map<string, number>();
      for (const ticket of tickets) {
        if (ticket.event?.uid !== summary.eventUid) continue;
        for (const leg of wallets.balances.get(ticket.uid) ?? []) {
          totals.set(
            leg.currencyCode,
            (totals.get(leg.currencyCode) ?? 0) + Number(leg.balance ?? 0)
          );
        }
      }
      summary.walletTotals = [...totals.entries()].map(([currencyCode, balance]) => ({
        currencyCode,
        balance,
      }));
    }

    return [...byEvent.values()].sort((a, b) => {
      const left = a.date ? Date.parse(a.date) : 0;
      const right = b.date ? Date.parse(b.date) : 0;
      return right - left;
    });
  }
}
